/**
 * AiModeCard — режим ассистента клиники: выключен, наблюдает (готовит
 * ответы, пациенту не пишет), черновики на проверку, отвечает сам.
 *
 * Текущий режим берём из карточки клиента. Раньше бэк его не отдавал, и
 * экран подсвечивал «последний выбранный в этой сессии» — то есть после
 * перезагрузки страницы всегда показывал «Выключен», каким бы режим ни
 * был на самом деле. Теперь поле есть, и догадка больше не нужна.
 *
 * Бэк умеет менять режим и сам — например гасит ассистента при долге, —
 * и объясняет это в `aiModeReason`. Причину показываем: без неё
 * «почему у клиники выключился бот» выясняли бы по логам.
 */

import {useMutation, useQueryClient} from '@tanstack/react-query';
import dayjs from 'dayjs';

import {type AiMode, setAiMode} from '@services/clients';
import {keys} from '@queries/keys';
import {cn} from '@lib/utils';

import {SectionCard} from '../../SectionCard';

const MODES: {key: AiMode; label: string; text: string}[] = [
  {key: 'off', label: 'Выключен', text: 'пациентам не отвечает'},
  {key: 'shadow', label: 'Наблюдаю', text: 'готовит ответы в тени'},
  {key: 'assisted', label: 'Проверяю', text: 'черновик на подтверждение'},
  {key: 'auto', label: 'Доверяю', text: 'отвечает сам'},
];

type Props = {
  clientId: string;
  mode: AiMode;
  reason: string | null;
  changedAt: string | null;
  onSaved: (text: string) => void;
};

export const AiModeCard = ({clientId, mode, reason, changedAt, onSaved}: Props) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: setAiMode,
    onSuccess: (m) => {
      void queryClient.invalidateQueries({queryKey: keys.clients.detail(clientId)});
      onSaved(`Режим ассистента: ${MODES.find((x) => x.key === m)?.label ?? m}`);
    },
  });

  return (
    <SectionCard
      title="Режим ассистента"
      hint={
        changedAt !== null ? `Изменён ${dayjs(changedAt).format('D MMM YYYY, HH:mm')}` : undefined
      }
    >
      <div className="grid grid-cols-2 gap-2">
        {MODES.map((m) => {
          const on = mode === m.key;
          const pending = mutation.isPending && mutation.variables?.mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({id: clientId, mode: m.key})}
              className={cn(
                'rounded-[11px] border p-[10px_12px] text-left transition disabled:opacity-60',
                on ? 'border-primary bg-primary/10' : 'border-skr-line hover:border-primary/40',
              )}
            >
              <div className={cn('text-[13.5px] font-semibold', on && 'text-primary')}>
                {pending ? 'Сохраняем…' : m.label}
              </div>
              <div className="mt-0.5 text-[11.5px] leading-[1.35] text-muted-foreground">
                {m.text}
              </div>
            </button>
          );
        })}
      </div>

      {reason !== null && (
        <div className="mt-2.5 rounded-[10px] bg-skr-head px-[13px] py-2 text-[12.5px] leading-[1.45] text-skr-ink2">
          {reason}
        </div>
      )}

      {mutation.isError && (
        <div className="mt-2.5 rounded-[10px] bg-destructive/10 px-3.5 py-2.5 text-[12.5px] font-medium text-destructive">
          Не удалось сменить режим. Попробуйте ещё раз
        </div>
      )}
    </SectionCard>
  );
};
